<script setup lang="ts">
import Box from '@uiKit/elements/Box.vue'
import Text from '@uiKit/elements/Text.vue'

interface FooterLink {
  label: string
  href: string
}

// Placeholder targets — swap for real policy routes when they land.
const links: FooterLink[] = [
  { label: 'Terms', href: '#' },
  { label: 'Privacy', href: '#' },
  { label: 'Regulatory', href: '#' },
]

const year = new Date().getFullYear()
</script>

<template>
  <Box is="footer" class="auth-footer" align="center" justify="between">
    <Text is="span" type="caption" color="muted">© {{ year }} Sliceal</Text>

    <Box class="auth-footer__links" align="center" gap="1">
      <template v-for="(link, index) in links" :key="link.label">
        <Text
          v-if="index > 0"
          is="span"
          type="caption"
          color="muted"
          class="auth-footer__separator"
          aria-hidden="true"
          >•</Text
        >
        <Text is="a" type="caption" color="muted" :href="link.href" class="auth-footer__link">
          {{ link.label }}
        </Text>
      </template>
    </Box>
  </Box>
</template>

<style scoped>
.auth-footer {
  /* Vertical padding tracks the viewport height so the legal strip stays
     pinned to the bottom of the shell without eating the form's budget on a
     short laptop. */
  padding: var(--auth-footer-pad-y, 0.75rem) 2.5rem;
  /* Owned here rather than via Box's `gap` prop: the prop renders a
     `.box--gap-*` class, which ties on specificity with the rule below and is
     resolved only by stylesheet order — fragile across a production build. */
  gap: 1rem;
  border-top: 1px solid var(--p-content-border-color);
  flex-shrink: 0;
  box-sizing: border-box;
}

.auth-footer__link {
  text-decoration: none;
  transition: color 0.15s ease;
}

.auth-footer__link:hover {
  color: var(--p-primary-600);
}

.auth-footer__separator {
  opacity: 0.4;
}

@media (max-width: 992px) {
  .auth-footer {
    /* Stays a single row. Stacking it reversed doubled the strip's height on a
       phone — ~62px of the form's budget for two lines of 12px legal text,
       more than it costs on desktop. The row fits comfortably at 360px.
       Vertical padding stays on the shared scale; only the gutter narrows. */
    gap: 0.75rem;
    padding: var(--auth-footer-pad-y, 0.625rem) 1rem;
  }
}
</style>
